import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Award, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { PACKAGES, CUSTOM_SOLUTION_COPY, BRAND } from "../data/site";

export default function Packages() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const whatsappHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent("Hi Zyvora Digital, I'd like a package quote.")}`;
  return (
    <div className="bg-[#0A0A0A] text-white pt-28 pb-24">
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[400px] w-[600px] rounded-full bg-[#F5D67B]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">All-In-One Packages</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl mx-auto">
            Bundled for{" "}
            <span className="text-gradient-brand">every stage of business.</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto">
            Choose the package that fits your stage. Each package can be customized to match your exact goals.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => (
            <div
              key={i}
              data-testid={`package-card-${i}`}
              className={`relative rounded-3xl p-8 lg:p-10 flex flex-col border transition-all ${
                p.popular
                  ? "border-[#D4AF37]/60 bg-[#1A1A1A] shadow-[0_0_60px_-10px_rgba(212,175,55,0.45)] md:-translate-y-3"
                  : "border-white/10 bg-[#121212] hover:border-[#D4AF37]/35"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-gradient-brand text-[#0A0A0A]">
                  Most Recommended
                </span>
              )}
              <div className="flex items-center gap-2 mb-3 text-[#D4AF37]">
                <Award className="h-5 w-5" />
                <Badge className="bg-white/5 text-[#F5D67B] border border-white/10">Best For {p.bestFor}</Badge>
              </div>
              <h3 className="font-display text-2xl font-bold mb-6">{p.name}</h3>
              <div className="mb-6">
                <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Includes</p>
                <ul className="space-y-3">
                  {p.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-200">
                      <CheckCircle2 className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex-1" />
              <Link to="/contact" data-testid={`package-cta-${i}`}>
                <Button
                  className={`w-full rounded-full h-12 font-semibold ${
                    p.popular
                      ? "bg-gradient-brand text-[#0A0A0A]"
                      : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  }`}
                >
                  Request Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Custom Solution */}
      <section data-testid="packages-custom-banner" className="mt-8">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl border border-white/10 bg-[#121212] p-8 md:p-12 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-brand opacity-10" />
            <div className="relative grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Need something else?</p>
                <h3 className="font-display text-2xl md:text-3xl font-bold leading-snug">
                  {CUSTOM_SOLUTION_COPY}
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3">
                <Link to="/contact" data-testid="packages-banner-cta">
                  <Button className="w-full bg-gradient-brand text-[#0A0A0A] rounded-full h-12 px-6 font-semibold">
                    Get Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="packages-banner-whatsapp">
                  <Button variant="outline" className="w-full border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full h-12 px-6 font-semibold">
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
